import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useFeathers } from '../../app/util';
import { useHistory } from 'react-router-dom';
import {
  CTAButton,
  SearchableSelectInput,
  CloseButton,
  HintTile,
  Checkbox,
  ElectionSelectInput
} from '../../components';
import moment from 'moment';
import { DesktopDatePicker as DatePicker } from '@mui/x-date-pickers';
import { 
  TextField, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Box,
  Typography 
} from '@mui/material';
import { setElections } from '../../app/actions';

const CreateElection = ({
  onClose,
  onSuccess
}) => {
  const feathers = useFeathers();
  const history = useHistory();
  const dispatch = useDispatch();
  const [ formInputs, setFormInputs ] = useState({
    name: '',
    type: '',
    district: null,
    date: null,
    runoffPrecedent: null,
    primaryPrecedent: null
  })
  const [ numRunoffRaces, setNumRunoffRaces ] = useState(undefined);
  const [ runoffConfirmed, setRunoffConfirmed ] = useState(false);
  const [ numRaces, setNumRaces ] = useState(undefined);
  const [ racesConfirmed, setRacesConfirmed ] = useState(false);
  const submittable = formInputs.name && formInputs.type && formInputs.district && formInputs.date;
  const onChange = e => {
    const { name, value } = e.target;
    const update = {};
    update[name] = value;
    let newInputs = {
      ...formInputs,
      ...update
    }
    setFormInputs(newInputs);
  }

  useEffect(() => {
    // Clear precedent fields when election type changes
    if (!['primary-runoff', 'general-runoff'].includes(formInputs.type)) {
      setFormInputs(prev => ({ ...prev, runoffPrecedent: null }));
    }
    if (formInputs.type !== 'general') {
      setFormInputs(prev => ({ ...prev, primaryPrecedent: null }));
    }
    
    if(['primary-runoff', 'general-runoff'].includes(formInputs.type)) {
      searchForField('runoffPrecedent', null)
    }
    if(formInputs.type === 'general') {
      searchForField('primaryPrecedent', null)
    }
    
    // Clear race confirmation when election type or precedent changes
    setNumRaces(undefined);
    setRacesConfirmed(false);
  }, [ formInputs.type, formInputs.runoffPrecedent, formInputs.primaryPrecedent ])

  /* manage searching options for selection, based on search text */
  const [ optionsForField, setOptionsForField ] = useState({});
  const [ loadingForField, setLoadingForField ] = useState({});
  const searchForField = async (field, searchText = '') => {
    if(!feathers) return;

    const loadingUpdate = {};
    loadingUpdate[field] = true;
    setLoadingForField({ ...loadingForField, ...loadingUpdate });

    let options = [];
    if(field === 'district') {
      const districts = await feathers.getService('districts').find({ query: {
        searchTerm: { $search: searchText }
      }})
      options = districts.data.map(dist => ({
        id: dist._id,
        text: dist.longName,
        subText: dist.parent || 'Statewide'
      }));
    } 
    const updateForOptions = {};
    updateForOptions[field] = options;
    setOptionsForField({ ...optionsForField, ...updateForOptions })

    const loadingUpdate2 = {};
    loadingUpdate2[field] = false;
    setLoadingForField({ ...loadingForField, ...loadingUpdate2 });
  }

  const [ submitting, setSubmitting ] = useState(false);
  const [ error, setError ] = useState(null);
  const [ stateElectionConfig, setStateElectionConfig ] = useState(null);
  // Whenever a district is selected, resolve the state and load its election configuration
  useEffect(() => {
    const loadStateConfigForDistrict = async () => {
      if (!feathers || !formInputs.district || !formInputs.district.id) {
        setStateElectionConfig(null);
        return;
      }

      try {
        const districtId = formInputs.district.id;
        const fullDistrict = await feathers.getService('districts').get(districtId, { query: { parentDepth: 5 } });

        // Mirror the logic used in submit() to resolve the state district
        let stateDistrict = fullDistrict.type === 'state' ? fullDistrict : fullDistrict.parentDistrict;
        if (stateDistrict && stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;
        if (stateDistrict && stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;
        if (stateDistrict && stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;

        if (!stateDistrict || stateDistrict.type !== 'state') {
          setStateElectionConfig(null);
          return;
        }

        const result = await feathers.getService('state-election-configurations').find({
          query: {
            state: stateDistrict._id,
            $limit: 1,
          },
          skipAuthentication: true,
        });

        const config = Array.isArray(result) ? result[0] : (result && result.data && result.data[0]);
        setStateElectionConfig(config || null);
      } catch (e) {
        console.error('Error loading state election configuration for district:', e);
        setStateElectionConfig(null);
      }
    };

    loadStateConfigForDistrict();
  }, [feathers, formInputs.district]);

  const submit = async () => {
    if(submitting || !submittable) return;
    // Create the candidate
  setSubmitting(true);
  setError(null);

    const districtId = formInputs.district?.id;
    const fullDistrict = await feathers.getService('districts').get(districtId, { query: { parentDepth: 5 } });
    let stateDistrict = fullDistrict.type === 'state' ? fullDistrict : fullDistrict.parentDistrict;
    if(stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;
    if(stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;
    if(stateDistrict.type !== 'state') stateDistrict = fullDistrict.parentDistrict;
  

    // Create the new election
    try {
      const creation = await feathers.getService('elections').create({
        status: 'prep',
        date: formInputs.date,
        name: formInputs.name,
        districts: [ formInputs.district?.id ],
        electionType: formInputs.type,
        state: stateDistrict._id,
        ...(formInputs?.runoffPrecedent ?  { runoffPrecedent: formInputs.runoffPrecedent.key } : {}),
        ...(formInputs?.primaryPrecedent ?  { primaryPrecedent: formInputs.primaryPrecedent.key } : {}),
        ...(runoffConfirmed ? { confirmRaces: numRunoffRaces } : {}),
        ...(racesConfirmed ? { confirmRaces: numRaces } : {}),
        setup: {
          dates: false,
          coverage: false,
          precedents: false
        }
      })
      // dispatch redux action to add the new election to the list
      dispatch((dispatch, getState) => {
        const currentState = getState();
        const currentElections = currentState?.elections || { byKey: {}, list: [], default: null };
        dispatch(setElections({
          ...currentElections,
          byKey: {
            ...currentElections.byKey,
            [creation.key]: creation
          }
        }));
      });
      if(onSuccess) {
        onSuccess(creation)
      } else {
        onClose()
      }
    } catch (error) {
      const errorAsJson = error.toJSON ? error.toJSON() : error;
      if(errorAsJson.code === 400 && typeof(errorAsJson?.data?.runoffRaces) !== 'undefined') {
        setNumRunoffRaces(errorAsJson?.data?.runoffRaces)
      } else if(errorAsJson.code === 400 && typeof(errorAsJson?.data?.numRaces) !== 'undefined') {
        setNumRaces(errorAsJson?.data?.numRaces)
      } else {
        console.log(error)
        setError({
          message: errorAsJson.message || 'Error creating this election.',
          details: errorAsJson?.data?.message || errorAsJson?.data?.error || null,
        })
      }
    } finally {
      setSubmitting(false)
    }
  }


  return (
    <Wrapper>
      <CloseButton
        onClick={onClose}
        style={{
          position: 'absolute',
          height: '36px',
          right: '-20px',
          top: '-20px'
        }}
      />
      <Top>
        <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
          New Election
        </Typography>
        
        <FormField>
          <TextField
            label="Name"
            name="name"
            value={formInputs.name}
            onChange={onChange}
            fullWidth
            size="small"
            required
          />
        </FormField>

        <FormField>
          <FormControl fullWidth size="small">
            <InputLabel>Election Type</InputLabel>
            <Select
              name="type"
              value={formInputs.type}
              onChange={onChange}
              label="Election Type"
              required
            >
              <MenuItem value="primary">Primary Election</MenuItem>
              <MenuItem value="primary-runoff">Primary Election Runoff</MenuItem>
              <MenuItem value="general">General Election</MenuItem>
              <MenuItem value="general-runoff">General Election Runoff</MenuItem>
              <MenuItem value="special">Special Election</MenuItem>
            </Select>
          </FormControl>
        </FormField>

        <FormField>
          <SearchableSelectInput
            name="district"
            onChangeSearchText={(searchText) => searchForField('district', searchText)}
            value={formInputs.district}
            onChange={onChange}
            optionsLoading={loadingForField.district}
            options={optionsForField?.district || []}
            style={{ width: '100%' }}
            placeholder="Bounding district"
          />
        </FormField>

        <FormField>
          <StyledDatePicker
            size="small"
            inputFormat="MM/DD/YYYY"
            label="Election Date"
            value={formInputs.date ? moment(formInputs.date) : null}
            onChange={(value) => onChange({ target: { name: 'date', value: value ? value.toDate() : null } })}
            renderInput={(params) => <TextField {...params} fullWidth required />}
          />
        </FormField>

        {['primary-runoff','general-runoff', 'general'].includes(formInputs.type) && (
          <FormField>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Original election
            </Typography>
            <ElectionSelectInput
              name={formInputs.type === 'general' ? 'primaryPrecedent' : 'runoffPrecedent'}
              value={formInputs.type === 'general' ? formInputs.primaryPrecedent : formInputs.runoffPrecedent}
              onChange={onChange}
              district={formInputs.district?.id}
              style={{ width: '100%' }}
            />
          </FormField>
        )}
        
        {stateElectionConfig && stateElectionConfig.setupComplete === false && (
          <Box sx={{ mb: 2 }}>
            <HintTile>
              <ErrorText>
                <b>Heads up! </b>
                The state election configuration for {stateElectionConfig.stateName || 'this state'} is not marked complete.{' '}
                You can edit it{' '}
                <span
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={() => {
                    if (stateElectionConfig.stateCode) {
                      history.push(`/states/${stateElectionConfig.stateCode.toLowerCase()}-state/state-config`);
                    } else {
                      history.push('/states');
                    }
                  }}
                >
                  here
                </span>.
              </ErrorText>
            </HintTile>
          </Box>
        )}

        {error && (
          <Box sx={{ mb: 2 }}>
            <HintTile>
              <ErrorText>
                <b>Uh oh! </b>
                Error creating this election: {error.message}
                {error.details && (
                  <>
                    <br />
                    <span>{error.details}</span>
                  </>
                )}
              </ErrorText>
            </HintTile>
          </Box>
        )}
        
        {typeof(numRunoffRaces) !== 'undefined' && (
          <Box sx={{ mb: 2 }}>
            <HintTile>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <b>Heads up! </b>{`If you do this, ${numRunoffRaces} races will be automatically created.`}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox
                    checked={runoffConfirmed}
                    onClick={() => setRunoffConfirmed(!runoffConfirmed)}
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    <b>Yes, proceed anyways</b>
                  </Typography>
                </Box>
              </Box>
            </HintTile>
          </Box>
        )}
        
        {typeof(numRaces) !== 'undefined' && (
          <Box sx={{ mb: 2 }}>
            <HintTile>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <b>Heads up! </b>{`Creating this election will automatically create ${numRaces} races, based on the previous election results.`}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <Checkbox
                    checked={racesConfirmed}
                    onClick={() => setRacesConfirmed(!racesConfirmed)}
                  />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    <b>Yes, proceed anyways</b>
                  </Typography>
                </Box>
              </Box>
            </HintTile>
          </Box>
        )}
        
        <Box sx={{ mt: 3 }}>
          <CTAButton
            value="Create"
            onClick={submit}
            disabled={!submittable || submitting}
          />
        </Box>
      </Top>
    </Wrapper>
  );

};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 500px;
  min-height: 200px;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormField = styled(Box)`
  margin-bottom: 24px;
  width: 100%;
`;

const ErrorText = styled.div`
  ${props => props.theme.font.normal}
  color: ${props => props.theme.colors.darkPurple};
  b {
    ${props => props.theme.font.bold}
  }
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  
  .MuiInputBase-input {
    padding: 8.5px 14px;
  }
`;

export default CreateElection;
